import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3d7c898e"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 1,
  class: "row justify-start reverse q-mt-md"
}

import { storeToRefs } from 'pinia';
import SafeLocalStorage from 'safe-localstorage';
import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import { type RecordableActionType, useHistoryStore, ACTION_TYPES } from '#src/store/history';
import ErrorBanner from 'components/ErrorBanner.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddNoteForm',
  props: {
    accountKey: { default: '' },
    actionType: { default: <RecordableActionType>ACTION_TYPES.MANUAL_ENTRY },
    autosave: { type: Boolean, default: true },
    clearOnCancel: { type: Boolean },
    flat: { type: Boolean },
    forceType: { default: undefined },
    hideActions: { type: Boolean },
    hint: { default: undefined },
    historyId: { default: undefined },
    historyIds: { default: undefined },
    label: { default: undefined },
    labelColor: { default: undefined },
    noteDetails: { default: undefined },
    saveButtonTarget: { default: undefined },
    saveButtonUrl: { default: undefined },
    saveActionText: { default: 'Add' },
    serial: { default: '' },
    update: { type: Boolean },
    userKey: { default: '' }
  },
  emits: ['cancel', 'emptied', 'error', 'focus', 'has-value', 'hide', 'input', 'save'],
  setup(__props: any, { expose, emit }) {

const props = __props as { accountKey: string, actionType: RecordableActionType, autosave: boolean, clearOnCancel?: boolean, flat?: boolean, forceType: 'account' | 'device' | 'return' | 'user', hideActions?: boolean, hint: string, historyId: number, historyIds: number[], label: string, labelColor: string, noteDetails: string, saveButtonTarget: string, saveButtonUrl: string, saveActionText: string, serial: string, update?: boolean, userKey: string };





expose({
  addNote,
});

const text = ref('');
const remainingChars = computed(() => 255 - text.value.length);
const addNoteError = ref(null as string | null);

const historyStore = useHistoryStore();
const { isSavingNote } = storeToRefs(historyStore);

const addNoteTextField = ref(null as HTMLTextAreaElement | null);
const addNoteForm = ref(null as HTMLFormElement | null);

async function addNote(event: Event) {
  addNoteError.value = null;

  if (text.value.length > 255) {
    setError('Please limit your note to 255 characters.');
    return;
  }

  const payload = {
    actionType: props.actionType,
    details: props.noteDetails,
    historyId: props.historyId,
    historyIds: props.historyIds,
    forceType: props.forceType,
    comment: text.value,
  } as {
    actionType?: RecordableActionType;
    details?: string;
    serial?: string;
    accountKey?: string;
    comment: string;
    userKey?: string;
  };

  if (props.serial) {
    payload.serial = props.serial;
  }

  if (props.userKey) {
    payload.userKey = props.userKey;
  }

  if (props.accountKey) {
    payload.accountKey = props.accountKey;
  }

  const { error, history } = await historyStore.addNote(payload);

  if (error) {
    setError(error);
    return;
  }

  emit('save', event, history);

  clear();
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    addNote(event);
    event.preventDefault();
  }
}

function onKeyup(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    emit('cancel');
    emit('hide');
  }

  if (text.value && props.autosave) {
    SafeLocalStorage.set('addNoteFormAutoSave', text.value);
  }

  emit('input', event, text.value);
}

function cancel() {
  emit('cancel');
  emit('hide');
  if (props.clearOnCancel || props.autosave) {
    clear();
  }
}

function clear() {
  text.value = '';
  SafeLocalStorage.remove('addNoteFormAutoSave');
}

function setError(error: string) {
  addNoteError.value = error;
  const invalidateError = watch(text, () => {
    invalidateError();
    addNoteError.value = null;
  });
  emit('error', addNoteError.value);
}

const isLoadingHistoryItem = ref(false);
async function getHistory() {
  isLoadingHistoryItem.value = true;
  const result = await historyStore.getHistoryById(<number>props.historyId);
  const error = (<{ error: string }>result).error;
  if (error) {
    setError(error);
  } else {
    text.value = (<HistoryItem>result).comment || '';
    addNoteTextField.value?.focus();
  }
  isLoadingHistoryItem.value = false;
}

onMounted(() => {
  if (props.autosave && !props.historyId) {
    text.value = SafeLocalStorage.get('addNoteFormAutoSave') || '';
  } else if (props.historyId) {
    getHistory();
  }
});

const hasValue = ref(false);
watchEffect(() => {
  hasValue.value = Boolean(text.value);
});

watchEffect(() => {
  if (hasValue.value) {
    emit('has-value');
  } else {
    emit('emptied');
  }
});

return (_ctx: any,_cache: any) => {
  const _component_q_input = _resolveComponent("q-input")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_form = _resolveComponent("q-form")!
  const _component_q_card = _resolveComponent("q-card")!

  return (_openBlock(), _createBlock(_component_q_card, {
    class: _normalizeClass(["z-index-1 column bg-transparent", { 'q-pa-sm': !__props.flat }]),
    flat: __props.flat
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_form, {
        ref_key: "addNoteForm",
        ref: addNoteForm,
        onSubmit: addNote
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_input, {
            ref_key: "addNoteTextField",
            ref: addNoteTextField,
            modelValue: text.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((text).value = $event)),
            autofocus: "",
            autogrow: "",
            "bg-color": "white",
            class: "field",
            color: "off-black",
            counter: "",
            disable: _unref(isSavingNote) || isLoadingHistoryItem.value,
            error: Boolean(addNoteError.value),
            hint: __props.hint,
            label: __props.label,
            "label-color": __props.labelColor,
            loading: isLoadingHistoryItem.value,
            outlined: "",
            type: "textarea",
            onKeydown: onKeydown,
            onKeyup: onKeyup
          }, {
            counter: _withCtx(() => [
              _createElementVNode("div", {
                class: _normalizeClass({ 'text-negative text-weight-bold': _unref(remainingChars) < 0 })
              }, _toDisplayString(_unref(remainingChars)) + "/255 ", 3)
            ]),
            _: 1
          }, 8, ["modelValue", "disable", "error", "hint", "label", "label-color", "loading"]),
          (addNoteError.value)
            ? (_openBlock(), _createBlock(ErrorBanner, {
                key: 0,
                class: "q-mt-sm",
                dense: "",
                message: addNoteError.value
              }, null, 8, ["message"]))
            : _createCommentVNode("", true),
          (!__props.hideActions)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _renderSlot(_ctx.$slots, "actions", {}, () => [
                  _createVNode(_component_q_btn, {
                    color: "primary",
                    disable: text.value.length === 0 || isLoadingHistoryItem.value,
                    href: __props.saveButtonUrl,
                    loading: _unref(isSavingNote),
                    target: __props.saveButtonUrl ? '_blank' : undefined,
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (addNoteForm.value?.submit()))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(__props.saveActionText), 1)
                    ]),
                    _: 1
                  }, 8, ["disable", "href", "loading", "target"])
                ]),
                _createVNode(_component_q_btn, {
                  class: "q-mr-sm text-off-black",
                  flat: "",
                  onClick: cancel
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Cancel")
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 3
      }, 512)
    ]),
    _: 3
  }, 8, ["class", "flat"]))
}
}

})